import type { ErrorEvent, EventHint } from './sentry-impl.js'

type Impl = typeof import('./sentry-impl.js')

const appId = 'wallet'
const dsn = import.meta.env.VITE_SENTRY_DSN

let impl: Impl | undefined
let initialized = false

async function load() {
  if (impl || !dsn) return impl
  // Imported lazily so the Sentry SDK lands in its own dynamic chunk. The
  // `sentry-impl` wrapper re-exports only `init` and `captureException` via
  // *named static* imports, which lets Rollup tree-shake the rest of the
  // `@sentry/browser` barrel before code-splitting.
  impl = await import('./sentry-impl.js')
  impl.init({
    dsn,
    enabled: true,
    environment: import.meta.env.MODE,
    initialScope: {
      tags: {
        tempo_app_id: appId,
        tempo_runtime: 'browser',
      },
    },
    release: __BUILD_VERSION__,
    tunnel: '/sentry-tunnel',
    beforeSend,
  })
  return impl
}

/** Adds Connect-specific metadata to browser Sentry events. */
export function beforeSend(event: ErrorEvent, _hint: EventHint): ErrorEvent {
  event.tags = {
    ...event.tags,
    route: typeof window === 'undefined' ? undefined : window.location.pathname,
  }

  return event
}

/** Initializes the browser Sentry client when configured. */
export async function init() {
  if (initialized || typeof window === 'undefined' || !dsn) return
  await load()
  initialized = true
}

/** Reports a browser exception to Sentry when enabled. */
export async function captureException(error: unknown) {
  await init()
  if (!initialized) return
  impl!.captureException(error)
}
